import type { RequestEvent } from 'solid-js/web';
import { createContext, ResourceReturn } from 'solid-js';
import { getRequestEvent } from 'solid-js/web';
import { SetStoreFunction, Store } from 'solid-js/store';
import { createAsync } from '@solidjs/router';

import type { PromiseResolve } from '~/constants';

export const RootContext = createContext<(fn: () => any) => any>(() => {});

export type SusRootState = {
  count: number;
  pending: PromiseResolve<boolean>;
  pendingDelay?: number;
  pendingTimer?: number;
  /** Top-level Suspense, e.g. route transitions. Child components must render at least one `Suspense`. */
  pendingLevelTop?: number;
  /** Second-level Suspense, e.g. tab switching under a route. Child components must render at least one `Suspense`. */
  pendingLevelSection?: number;
  /** Deeper Suspense. Child components must render at least one `Suspense`; if not, use the `minDelay` prop on `Suspense` instead. */
  pendingLevelThree?: number;
};

export const SusRootContext = createContext<[SusRootState, SetStoreFunction<SusRootState>]>([
  { count: 0, pending: () => void 0 },
  () => void 0,
]);

export const useSusRootContext = () => {
  return useContext(SusRootContext);
};

export type SusSectionPendingParams = {
  trigger: Accessor<any>;
  level?: 'section' | 'three';
  delay?: number;
};
export const useSusSectionPending = ({ trigger, level = 'section', delay = 380 }: SusSectionPendingParams) => {
  const [susRoot, setSusRoot] = useSusRootContext();
  const [pending] = createResource(
    () => {
      switch (level) {
        case 'three':
          return susRoot.pendingLevelThree;
        default:
          return susRoot.pendingLevelSection;
      }
    },
    () => {
      return new Promise<boolean>((resolve) => {
        if (import.meta.env.SSR) {
          resolve(false);
        } else {
          setSusRoot({
            pending: resolve,
            pendingDelay: delay,
            pendingTimer: new Date().getTime(),
          });
        }
      });
    }
  );

  createEffect(
    on(
      trigger,
      (isTrigger) => {
        if (isTrigger) {
          let key: keyof SusRootState;
          switch (level) {
            case 'three':
              key = 'pendingLevelThree';
              break;
            default:
              key = 'pendingLevelSection';
              break;
          }
          // console.log('===walker===isTrigger', susRoot.count - 1);
          setSusRoot('count', (p) => p - 1);
          setSusRoot(key, new Date().getTime());
        }
      },
      { defer: true }
    )
  );

  return { pending };
};

/**
 * Create a shared root.
 * Similar to createSharedRoot from @solid-primitives/rootless,
 * but the rootless version has a bug that causes memory leaks during SSR.
 * This root runs at the top level and is never disposed on the client.
 * @param factory
 * @returns
 *
 * const useXxx = createSharedRoot(() => {});
 * const xxx = useXxx();
 */

export function createSharedRoot<T>(fn: () => T): () => T {
  return () => {
    return useContext(RootContext)(fn);
  };
}

/** Create a synchronous global store */
export function createResolvedStore<T extends object = {}>(source: () => T) {
  return createShareProxy<T>(() => {
    const [resolvedSource] = createResource(() => {
      return source();
    }) as unknown as ResourceReturn<T>;

    const store = createStore<T>(resolvedSource()!);
    const [, setter] = store;
    createEffect(() => setter(resolvedSource()!));
    return store;
  });
}

/** Create an async global store */
export function createAsyncedStore<T extends object = {}>(fetcher: () => Promise<T>) {
  return createShareProxy<T>(() => {
    const source = createAsync<T>(fetcher, { deferStream: true });
    const _source = source();
    if (_source === undefined) {
      return undefined;
    }
    const store = createStore(_source as any);
    const [, setter] = store;
    createEffect(() => setter(source()!));
    return store;
  });
}

/** Get the global context during hydration */
export function hydrationGlobal(): RequestEvent['locals'] | undefined {
  return import.meta.env.SSR ? getRequestEvent()?.locals : globalThis;
}

/** Map a shared proxy to the global context */
export function createShareProxy<T extends object = {}>(
  source: Accessor<[get: Store<T>, set: SetStoreFunction<T>] | undefined>
): [T, () => Store<T> | undefined, SetStoreFunction<T>] {
  const getkey = Symbol();
  const setkey = Symbol();
  const read = () => hydrationGlobal()?.[getkey];

  return [
    new Proxy(
      {},
      {
        get(_, props) {
          return read()?.[props];
        },
        ownKeys() {
          return Reflect.ownKeys(read());
        },
        getOwnPropertyDescriptor(_, prop) {
          return {
            enumerable: true,
            configurable: true,
            value: read()?.[prop],
          };
        },
      }
    ) as any,
    function mount() {
      const global = hydrationGlobal();
      const _source = source();
      if (global && _source) {
        const [store, setter] = _source;
        global[getkey] = store;
        global[setkey] = setter;
        return store;
      }
    },
    (...args: any) => {
      const global = hydrationGlobal();
      return global?.[setkey](...args);
    },
  ];
}
